import { CheckCircle, Error, Info, Warning } from "@mui/icons-material";
import { Box, Skeleton, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { IErrorResponse } from "../../interfaces/all";
import "./Toast.css";

export interface ToastProps {
  type: "success" | "error" | "warning" | "info";
  message: string;
  onClose: () => void;
  duration?: number;
  showSkeleton?: boolean;
}

export interface ToastContainerProps {
  toasts: ToastProps[];
  onClose: (index: number) => void;
  position?: "top-right" | "top-left" | "bottom-right" | "bottom-left";
  theme?: "light" | "dark";
}

const getToastIcon = (type: ToastProps["type"]) => {
  switch (type) {
    case "success":
      return <CheckCircle />;
    case "error":
      return <Error />;
    case "warning":
      return <Warning />;
    case "info":
      return <Info />;
    default:
      return <Info />;
  }
};

const getToastClassName = (type: ToastProps["type"], isExiting: boolean) => {
  return `toast toast-${type} ${isExiting ? "toast-exit" : ""}`;
};

export const Toast: React.FC<ToastProps> = ({
  type,
  message,
  onClose,
  duration = 5000,
  showSkeleton = false,
}) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const exitTimer = setTimeout(() => {
      setIsExiting(true);
    }, duration);

    const closeTimer = setTimeout(() => {
      onClose();
    }, duration + 300);

    return () => {
      clearTimeout(exitTimer);
      clearTimeout(closeTimer);
    };
  }, [duration, onClose]);

  if (showSkeleton) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          padding: "12px 16px",
          borderRadius: "8px",
          backgroundColor: "#ffffff",
          border: "1px solid #e0e0e0",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          minWidth: "300px",
        }}
      >
        <Skeleton variant="circular" width={20} height={20} />
        <Skeleton variant="text" width="80%" height={16} />
      </Box>
    );
  }

  return (
    <div className={getToastClassName(type, isExiting)}>
      <div className="toast-icon">{getToastIcon(type)}</div>
      <div className="toast-message">
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {message}
        </Typography>
      </div>
      <button className="toast-close" onClick={onClose}>
        ×
      </button>
    </div>
  );
};

export const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  onClose,
  position = "top-right",
  theme = "dark",
}) => {
  return (
    <div className={`toast-container toast-${position} toast-theme-${theme}`}>
      {toasts.map((toast, index) => (
        <Toast
          key={`toast-${toast.type}-${toast.message.substring(0, 10)}-${index}`}
          {...toast}
          onClose={() => onClose(index)}
        />
      ))}
    </div>
  );
};

export const showErrorToast = (error: IErrorResponse): ToastProps => ({
  type: "error",
  message: error.userMessage || "Erro desconhecido",
  onClose: () => {},
});

export const showSuccessToast = (message: string): ToastProps => ({
  type: "success",
  message,
  onClose: () => {},
});
